import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSnapshot } from 'valtio';

import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import { hasPremiumFeatures, isPro } from '@cartwhl/common/stores/userStore';
import { StripeCustomer } from '@cartwhl/common/types';
import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import {
  formatOrgCreditsForDisplay,
  formatOrganizationPlanType,
  getCartwheelApiKeysUpgradeMessaging,
  getOrgAvailableCredits,
  getStripeCustomerIdForBilling,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import FormattedDate from '@cartwhl/ui/atoms/FormattedDate';
import CurrentPlanFeature, {
  type CurrentPlanFeatureAction,
} from '@cartwhl/ui/feature/CurrentPlan';
import ArrowIcon from '@cartwhl/ui/icons/ArrowIcon';
import DiscordIcon from '@cartwhl/ui/icons/DiscordIcon';
import MailIcon from '@cartwhl/ui/icons/MailIcon';
import SpinIcon from '@cartwhl/ui/icons/SpinIcon';
import Button from '@cartwhl/ui/input/Button';
import PurchaseSpinsModal from '@cartwhl/ui/modals/PurchaseSpinsModal';
import SpinsExplainedModal from '@cartwhl/ui/modals/SpinsExplainedModal';

import {
  customerStore,
  noticesStore,
  productStore,
  userStore,
} from 'stores/global';

import { PRICING_CREDITS_EXPLAINER_ID } from '../../PricingPage/components/PricingCreditsExplainerSection';

function isOrgTeamSeatPlan(planType: string | undefined): boolean {
  return (
    planType === 'team_standard' ||
    planType === 'team_pro' ||
    planType === 'team_scale'
  );
}

function isIndividualBasicOrgPlan(planType: string | undefined): boolean {
  return planType === 'individual_basic';
}

function createUpgradeAction(
  navigate: ReturnType<typeof useNavigate>,
  organizationPlanType: string | undefined,
): CurrentPlanFeatureAction {
  const pricingPath = isIndividualBasicOrgPlan(organizationPlanType)
    ? getCartwheelApiKeysUpgradeMessaging('individual_basic').pricingPath
    : '/pricing';

  return {
    label: 'Upgrade',
    onClick: () => navigate(pricingPath),
    variant: 'tertiary',
    size: 'lg',
    className:
      'bg-gray-90 dark:bg-gray-90 hover:bg-gray-75 dark:hover:bg-gray-75 active:bg-gray-60 dark:active:bg-gray-60 text-white dark:text-white',
  };
}

export default function CurrentPlan() {
  const navigate = useNavigate();
  const { loading: productsLoading } = useSnapshot(productStore.state);
  const { customer, loading: customerLoading } = useSnapshot(
    customerStore.state,
  );
  const { organizationInfo, userInfo } = useSnapshot(userStore.state);
  const [cancelTrialModalOpen, setCancelTrialModalOpen] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [spinsExplainedOpen, setSpinsExplainedOpen] = useState(false);
  const [purchaseSpinsOpen, setPurchaseSpinsOpen] = useState(false);

  const orgCreditsWorkspace = isOrgCreditsWorkspace(organizationInfo);
  const orgAvailableCredits = getOrgAvailableCredits(organizationInfo);

  const subscriberPremiumStatus = customerStore.getSubscriberPremiumStatus(
    customer as StripeCustomer | null,
  );

  const hasActiveSubscription =
    subscriberPremiumStatus === 'isPaying' &&
    userInfo.premiumAccessUntil &&
    userInfo.premiumAccessUntil > new Date().toJSON();

  const spinsButtonStyle = hasActiveSubscription
    ? 'bg-gray-90 dark:bg-gray-90 hover:bg-gray-75 dark:hover:bg-gray-75 active:bg-gray-60 dark:active:bg-gray-60 text-white dark:text-white'
    : 'bg-gray-45/35 dark:bg-gray-45/35 hover:bg-gray-60/35 dark:hover:bg-gray-60/35 active:bg-gray-75/35 dark:active:bg-gray-75/35 dark:text-gray-90';

  const stripeCustomerIdForBilling = getStripeCustomerIdForBilling(
    userInfo,
    organizationInfo,
  );

  async function openStripeBillingPortal() {
    if (!stripeCustomerIdForBilling || billingPortalLoading) {
      return;
    }
    setBillingPortalLoading(true);
    try {
      const { data } = await getStripePortalSession(stripeCustomerIdForBilling);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch {
      noticesStore.error('Could not open billing portal. Please try again.');
    }
    setBillingPortalLoading(false);
  }

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  useEffect(() => {
    if (userInfo.stripeCustomerID) {
      customerStore.fetchCustomer(userInfo.stripeCustomerID);
    }
  }, [userInfo.stripeCustomerID]);

  let currentPlanTitle = 'Creator';
  let currentPlanMeta: string | undefined;

  if (organizationInfo?.organizationName) {
    currentPlanTitle = organizationInfo.organizationName;
    currentPlanMeta = `(${organizationInfo.accessLevel === 'EDIT' ? 'Admin' : 'Member'} / ${
      userInfo.planType === 'unlimited'
        ? 'Unlimited'
        : userInfo.planType === 'pro'
          ? 'Professional'
          : 'Free'
    }${organizationInfo.inStripeFreeTrialPeriod ? ' (trial)' : ''})`;
  } else if (userInfo.planType === 'unlimited') {
    currentPlanTitle = 'Advanced';
  } else if (userInfo.planType === 'pro') {
    currentPlanTitle = 'Professional';
  } else if (hasPremiumFeatures()) {
    currentPlanTitle = 'Premium';
  }

  const mailtoAddress =
    userInfo.planType === 'free'
      ? 'support@getcartwheel.com'
      : 'pros@getcartwheel.com';

  const supportActions: CurrentPlanFeatureAction[] = [
    {
      label: 'Contact support',
      href: `mailto:${mailtoAddress}?subject=Help%20with%20Account%20Status`,
      target: '_blank',
      rel: 'noopener noreferrer',
      icon: <MailIcon />,
      variant: 'secondary',
      size: 'lg',
    },
    {
      label: 'Get help on Discord',
      href: 'https://discord.gg/QGRBqwJakb',
      target: '_blank',
      rel: 'noopener noreferrer',
      icon: <DiscordIcon />,
      variant: 'secondary',
      size: 'lg',
    },
  ];

  if (!orgCreditsWorkspace && (productsLoading || customerLoading)) {
    return <CurrentPlanFeature state="loading" />;
  }

  const orgPlanLabel = organizationInfo
    ? formatOrganizationPlanType(organizationInfo.planType)
    : '';
  const orgBillingPeriodLabel =
    organizationInfo?.billingCycle === 'yearly' ? 'yearly' : 'monthly';

  if (orgCreditsWorkspace && organizationInfo) {
    const orgPurchasedSeats = Math.max(
      1,
      organizationInfo.seatsPurchased ?? organizationInfo.activeSeats ?? 1,
    );
    const detailItems = organizationInfo.inStripeFreeTrialPeriod
      ? organizationInfo.nextBillingDate
        ? [
            {
              label: 'Free trial ends',
              value: (
                <span className="font-semibold">
                  <FormattedDate date={organizationInfo.nextBillingDate} />
                </span>
              ),
            },
          ]
        : []
      : [
          {
            label: 'Next credit refresh',
            value: (
              <FormattedDate
                date={organizationInfo.nextAutomatedCreditRefreshDate ?? ''}
              />
            ),
          },
          ...(organizationInfo.nextBillingDate
            ? [
                {
                  label: 'Next billing date',
                  value: (
                    <FormattedDate date={organizationInfo.nextBillingDate} />
                  ),
                },
              ]
            : []),
        ];

    const primaryActions: CurrentPlanFeatureAction[] =
      organizationInfo.inStripeFreeTrialPeriod
        ? [
            {
              label: 'Cancel trial',
              onClick: () => setCancelTrialModalOpen(true),
              disabled: !stripeCustomerIdForBilling,
              variant: 'tertiary',
              size: 'lg',
              className:
                'bg-gray-45/35 dark:bg-gray-45/35 hover:bg-gray-60/35 dark:hover:bg-gray-60/35',
            },
            {
              label: 'Activate plan early',
              onClick: () => {
                updatedProductStore.openActivateEarlyTrialModal();
              },
              variant: 'tertiary',
              size: 'lg',
              className:
                'bg-gray-90 dark:bg-gray-90 hover:bg-gray-75 dark:hover:bg-gray-75 active:bg-gray-60 dark:active:bg-gray-60 text-white dark:text-white',
            },
          ]
        : [
            {
              label: 'Buy more credits',
              onClick: () => {
                updatedProductStore.openPurchaseCreditsModal({
                  variant:
                    orgAvailableCredits <= 0 ? 'out_of_credits' : 'low_credit',
                  creditsRemaining: orgAvailableCredits,
                });
              },
              variant: 'tertiary',
              size: 'lg',
              className:
                'bg-gray-90 dark:bg-gray-90 hover:bg-gray-75 dark:hover:bg-gray-75 active:bg-gray-60 dark:active:bg-gray-60 text-white dark:text-white',
            },
          ];

    if (isIndividualBasicOrgPlan(organizationInfo.planType)) {
      primaryActions.push(
        createUpgradeAction(navigate, organizationInfo.planType),
      );
    }

    const orgCreditsFreeTrialModal = organizationInfo.inStripeFreeTrialPeriod
      ? {
          open: cancelTrialModalOpen,
          onOpenChange: (open: boolean) => {
            if (!open) {
              setCancelTrialModalOpen(false);
              setBillingPortalLoading(false);
            }
          },
          title: 'Cancel your free trial?',
          description:
            'You’ll continue to Stripe’s billing portal, where you can cancel your trial or manage your subscription.',
          maxWidth: 440,
          footer: (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="secondary"
                disabled={billingPortalLoading}
                onClick={() => setCancelTrialModalOpen(false)}
              >
                Keep trial
              </Button>
              <Button
                variant="primary"
                loading={billingPortalLoading}
                disabled={!stripeCustomerIdForBilling}
                onClick={openStripeBillingPortal}
              >
                Continue to billing portal
              </Button>
            </div>
          ),
        }
      : undefined;

    return (
      <CurrentPlanFeature
        state="org-credits"
        planEyebrow="Current plan"
        planTitle={
          organizationInfo.inStripeFreeTrialPeriod
            ? '7-day free trial'
            : isOrgTeamSeatPlan(organizationInfo.planType)
              ? `${orgPurchasedSeats}x ${orgPlanLabel}`
              : orgPlanLabel
        }
        planTitleVariant={
          organizationInfo.inStripeFreeTrialPeriod ? 'trial' : 'default'
        }
        planMeta={
          organizationInfo.inStripeFreeTrialPeriod ? (
            <>
              {isOrgTeamSeatPlan(organizationInfo.planType)
                ? `${orgPurchasedSeats}x ${orgPlanLabel}`
                : orgPlanLabel}{' '}
              <span className="text-gray-60">({orgBillingPeriodLabel})</span>
            </>
          ) : (
            `Billed ${orgBillingPeriodLabel}`
          )
        }
        balanceEyebrow={`${organizationInfo.inStripeFreeTrialPeriod ? 'Trial Period Credit Balance' : 'Credit Balance'}`}
        balanceValue={formatOrgCreditsForDisplay(orgAvailableCredits)}
        balanceIcon={<SpinIcon className="h-5 w-5 shrink-0" />}
        balanceFootnote={
          <a
            href={`${getHomepageUrl('/pricing')}#${PRICING_CREDITS_EXPLAINER_ID}`}
            className="text-blue text-sm font-medium underline"
          >
            How credits work
          </a>
        }
        detailItems={detailItems}
        detailNote={
          !organizationInfo.inStripeFreeTrialPeriod
            ? `${formatOrgCreditsForDisplay(
                organizationInfo.monthlyCreditRefreshAmount!,
              )} credits refresh each billing cycle`
            : `${formatOrgCreditsForDisplay(
                organizationInfo.monthlyCreditRefreshAmount! -
                  updatedProductStore.ORG_TRIAL_ORG_CREDIT_STARTER_AMOUNT,
              )} credits unlock after the trial is over`
        }
        detailLayout="split"
        primaryActions={primaryActions}
        supportActions={supportActions}
        modal={orgCreditsFreeTrialModal}
      />
    );
  }

  const primaryActions: CurrentPlanFeatureAction[] = [
    {
      label: 'Buy more Spins',
      onClick: () => setPurchaseSpinsOpen(true),
      variant: 'tertiary',
      size: 'lg',
      className: spinsButtonStyle,
    },
  ];

  if (
    !hasActiveSubscription ||
    isIndividualBasicOrgPlan(organizationInfo?.planType)
  ) {
    primaryActions.push(
      createUpgradeAction(navigate, organizationInfo?.planType),
    );
  }

  return (
    <>
      <CurrentPlanFeature
        state="standard"
        planEyebrow={
          subscriberPremiumStatus === 'willExpire'
            ? 'Expiring plan'
            : 'Current plan'
        }
        planTitle={currentPlanTitle}
        planMeta={currentPlanMeta}
        balanceEyebrow="Spin balance"
        balanceValue={`${userInfo.spinsAvailable} Spins`}
        balanceAccessory={
          <Button
            variant="secondary"
            className="bg-gray-45/35 dark:bg-gray-45/35 hover:bg-gray-60/35 dark:hover:bg-gray-60/35 active:bg-gray-75/35 dark:active:bg-gray-75/35 ml-2"
            size="sm"
            onClick={() => setSpinsExplainedOpen(true)}
            icon={<ArrowIcon className="text-gray-90 -rotate-45" />}
          />
        }
        scheduleEyebrow={
          subscriberPremiumStatus === 'willExpire'
            ? 'Expires on'
            : 'Next billing cycle'
        }
        scheduleValue={
          isPro() ? (
            <FormattedDate date={userInfo.premiumAccessUntil} />
          ) : (
            <FormattedDate date={userInfo.nextMonthlyRefreshEligibleAt} />
          )
        }
        primaryActions={primaryActions}
        supportActions={supportActions}
      />
      <SpinsExplainedModal
        open={spinsExplainedOpen}
        onOpenChange={setSpinsExplainedOpen}
      />
      <PurchaseSpinsModal
        open={purchaseSpinsOpen}
        onOpenChange={setPurchaseSpinsOpen}
      />
    </>
  );
}
